import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Star, Users, Lock, FileText, X } from 'lucide-react';
import { getPool, getReviews, getReviewTeksten, ReviewScores, ReviewRij, ReviewLaag } from '../../services/dataCockpitApi';
import { usePeriode } from '../../store/dataPeriodeStore';
import { DataFilterBalk, PrivacyOnderdrukt, DataKop, Kaart, Laden, Fout } from '../../components/data/DataFilterBalk';
import { DonutChart, REEKS_KLEUREN } from '../../components/data/DataCharts';

const OVERIG_KLEUR = '#9ca3af';

const InzetTab: React.FC = () => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const q = useQuery({ queryKey: ['data-pool', ...key], queryFn: () => getPool(periode) });
  const d = q.data;
  if (q.isLoading) return <Laden />;
  if (q.isError) return <Fout fout={q.error} />;
  if (!d) return null;
  if (d.grendel.onderdrukt) return <PrivacyOnderdrukt grendel={d.grendel} />;

  // Kleur volgt de discipline (vaste volgorde), maximaal 7 + "overig"
  const zichtbaar = d.segmenten.slice(0, 7);
  const gevouwen = d.segmenten.slice(7);
  const overigN = (d.overig?.meldingen ?? 0) + gevouwen.reduce((s, x) => s + x.meldingen, 0);
  const overigUren = (d.overig?.uren ?? 0) + gevouwen.reduce((s, x) => s + x.uren, 0);
  const segmenten = [
    ...zichtbaar.map((s, i) => ({ label: s.label, waarde: s.meldingen, uren: s.uren, kleur: REEKS_KLEUREN[i] })),
    ...(overigN > 0 && !d.overig?.onderdrukt ? [{ label: t('dataCockpit.pool.overig'), waarde: overigN, uren: overigUren, kleur: OVERIG_KLEUR }] : []),
  ];

  return (
    <div className="space-y-6">
      <Kaart titel={t('dataCockpit.pool.verdeling')} uitleg={t('dataCockpit.pool.verdelingUitleg')}>
        <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
          <DonutChart segmenten={segmenten} centrumWaarde={String(d.totaal ?? 0)} centrumLabel={t('dataCockpit.pool.meldingen')} />
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium">{t('dataCockpit.pool.discipline')}</th>
                <th className="py-2 pr-3 text-right font-medium">{t('dataCockpit.pool.meldingen')}</th>
                <th className="py-2 text-right font-medium">{t('dataCockpit.pool.uren')}</th>
              </tr>
            </thead>
            <tbody>
              {segmenten.map((s) => (
                <tr key={s.label} className="border-b border-gray-100">
                  <td className="py-2 pr-3"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: s.kleur }} />{s.label}</td>
                  <td className="py-2 pr-3 text-right font-medium">{s.waarde}</td>
                  <td className="py-2 text-right">{s.uren.toLocaleString('nl-NL')}</td>
                </tr>
              ))}
              {d.overig?.onderdrukt && (
                <tr><td colSpan={3} className="py-2 text-xs text-amber-800"><Lock className="mr-1 inline h-3 w-3" aria-hidden />{t('dataCockpit.pool.overigOnderdrukt')}</td></tr>
              )}
              <tr>
                <td className="py-2 pr-3 font-semibold">{t('dataCockpit.pool.totaal')}</td>
                <td className="py-2 pr-3 text-right font-semibold">{d.totaal}</td>
                <td className="py-2 text-right font-semibold">{(d.totaal_uren ?? 0).toLocaleString('nl-NL')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Kaart>

      <div className="grid gap-6 lg:grid-cols-2">
        <Kaart titel={t('dataCockpit.pool.perAfdeling')} uitleg={t('dataCockpit.pool.perAfdelingUitleg')}>
          {d.per_afdeling.length === 0 ? (
            <p className="text-sm text-gray-500">{t('dataCockpit.pool.geenData')}</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {d.per_afdeling.map((r) => (
                  <tr key={`${r.discipline}-${r.afdeling}`} className="border-b border-gray-100">
                    <td className="py-2 pr-3">{r.label}</td>
                    <td className={`py-2 pr-3 ${r.samengevoegd ? 'italic text-gray-500' : ''}`}>{r.afdeling}</td>
                    <td className="py-2 text-right font-medium">{r.meldingen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Kaart>

        <Kaart titel={t('dataCockpit.pool.feed')} uitleg={t('dataCockpit.pool.feedUitleg')}>
          <ul className="max-h-96 space-y-1 overflow-y-auto pr-1">
            {d.feed.map((m, i) => (
              <li key={`${m.code}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-lg px-2 py-1.5 text-sm hover:bg-gray-50">
                <span className="whitespace-nowrap text-gray-500">📅 {new Date(m.datum).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                <span className="font-mono text-xs text-gray-700">{m.code}</span>
                <span className="text-gray-900">{m.discipline}</span>
                <span className={`ml-auto text-xs font-medium ${m.gekoppeld ? 'text-green-700' : 'text-amber-700'}`}>
                  {m.gekoppeld ? `🟢 ${t('dataCockpit.pool.gekoppeld')}` : `🟡 ${t('dataCockpit.pool.wachtOpKoppeling')}`}
                </span>
              </li>
            ))}
          </ul>
        </Kaart>
      </div>
    </div>
  );
};

const Sterren: React.FC<{ v: number | null }> = ({ v }) =>
  v == null ? <span className="text-gray-400">—</span> : <span className="whitespace-nowrap"><Star className="mr-0.5 inline h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />{v.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>;

const LAAG_ICOON: Record<ReviewLaag, string> = { intern: '🏢', landelijk: '🌐' };

/** Tekstfeedback van één laag; de server controleert de grendel opnieuw. */
const TekstenModal: React.FC<{ rij: ReviewRij; laag: ReviewLaag; onSluit: () => void }> = ({ rij, laag, onSluit }) => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const q = useQuery({
    queryKey: ['data-review-teksten', rij.professional_id, laag, ...key],
    queryFn: () => getReviewTeksten(rij.professional_id, laag, periode),
    retry: false,
  });
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onSluit();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSluit]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onSluit}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-teksten-titel"
        className="mt-12 w-full max-w-2xl rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id="review-teksten-titel" className="text-base font-semibold text-gray-900">
              {LAAG_ICOON[laag]} {t(`dataCockpit.reviews.modal.titel.${laag}`, { naam: rij.naam })}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">{t('dataCockpit.reviews.modal.sanitizer')}</p>
          </div>
          <button type="button" onClick={onSluit} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label={t('dataCockpit.reviews.modal.sluiten')}>
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">
          {q.isLoading ? (
            <Laden />
          ) : q.isError ? (
            <Fout fout={q.error} />
          ) : !q.data?.teksten.length ? (
            <p className="py-6 text-center text-sm text-gray-500">{t('dataCockpit.reviews.modal.geenTekst', { n: q.data?.aantal_reviews ?? 0 })}</p>
          ) : (
            <ul className="space-y-3">
              {q.data.teksten.map((tekst, i) => (
                <li key={i} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-800">
                  <p className="whitespace-pre-wrap">&ldquo;{tekst}&rdquo;</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const ReviewsTab: React.FC = () => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const [laag, setLaag] = useState<ReviewLaag>('intern');
  // Per professional kan HR afwijken van de globale laag
  const [perRij, setPerRij] = useState<Record<string, ReviewLaag>>({});
  const [open, setOpen] = useState<{ rij: ReviewRij; laag: ReviewLaag } | null>(null);
  const sluit = useCallback(() => setOpen(null), []);
  const q = useQuery({ queryKey: ['data-reviews', ...key], queryFn: () => getReviews(periode) });
  const d = q.data;
  if (q.isLoading) return <Laden />;
  if (q.isError) return <Fout fout={q.error} />;
  if (!d) return null;
  const n = d.minimum_reviews;

  const kiesGlobaal = (l: ReviewLaag) => {
    setLaag(l);
    setPerRij({});
  };

  return (
    <>
      <Kaart
        titel={t('dataCockpit.reviews.titel')}
        uitleg={t(`dataCockpit.reviews.uitleg.${laag}`, { n, org: d.minimum_organisaties })}
        actie={
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5" role="tablist" aria-label={t('dataCockpit.reviews.laagKeuze')}>
            {(['intern', 'landelijk'] as const).map((l) => (
              <button
                key={l}
                type="button"
                role="tab"
                aria-selected={laag === l}
                onClick={() => kiesGlobaal(l)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium ${laag === l ? 'bg-[#0B1F3A] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {LAAG_ICOON[l]} {t(`dataCockpit.reviews.laag.${l}`)}
              </button>
            ))}
          </div>
        }
      >
        {d[laag].length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">{t('dataCockpit.reviews.geen')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                  <th className="py-2 pr-3 font-medium">{t('dataCockpit.reviews.professional')}</th>
                  {d.dimensies.map((dim, i) => (
                    <th key={dim.key} className="py-2 pr-3 font-medium">{t('dataCockpit.reviews.pijler', { n: i + 1 })}: {t(`dataCockpit.reviews.dim.${dim.key}`)}</th>
                  ))}
                  <th className="py-2 pr-3 font-medium">{t('dataCockpit.reviews.viewStatus')}</th>
                  <th className="py-2 font-medium">{t('dataCockpit.reviews.actie')}</th>
                </tr>
              </thead>
              <tbody>
                {d[laag].map((r) => {
                  const rijLaag = perRij[r.professional_id] ?? laag;
                  const s: ReviewScores = r[rijLaag];
                  return (
                    <tr key={r.professional_id} className="border-b border-gray-100 align-top">
                      <td className="py-2.5 pr-3">
                        <p className="font-medium text-gray-900">{r.naam}</p>
                        {r.discipline && <p className="text-xs text-gray-500">{r.discipline}</p>}
                      </td>
                      {(['vakkennis', 'punctualiteit', 'doelgerichtheid'] as const).map((k) => (
                        <td key={k} className="py-2.5 pr-3">
                          {s.onderdrukt ? (
                            <span className="whitespace-nowrap text-xs text-gray-400"><Lock className="mr-1 inline h-3 w-3" aria-hidden />{t('dataCockpit.reviews.verborgen')}</span>
                          ) : (
                            <Sterren v={s[k]} />
                          )}
                        </td>
                      ))}
                      <td className="py-2.5 pr-3">
                        <div className="mb-1 inline-flex rounded-md border border-gray-200 p-0.5" role="group" aria-label={t('dataCockpit.reviews.laagKeuzeRij', { naam: r.naam })}>
                          {(['intern', 'landelijk'] as const).map((l) => {
                            const dicht = r[l].onderdrukt;
                            return (
                              <button
                                key={l}
                                type="button"
                                aria-pressed={rijLaag === l}
                                onClick={() => setPerRij((m) => ({ ...m, [r.professional_id]: l }))}
                                title={t(`dataCockpit.reviews.laag.${l}`)}
                                className={`rounded px-1.5 py-0.5 text-xs ${rijLaag === l ? 'bg-[#0B1F3A] text-white' : dicht ? 'text-gray-400 hover:bg-gray-50' : 'text-gray-600 hover:bg-gray-50'}`}
                              >
                                {dicht ? '🔒' : LAAG_ICOON[l]}
                                <span className="sr-only"> {t(`dataCockpit.reviews.laag.${l}`)}</span>
                              </button>
                            );
                          })}
                        </div>
                        <p className={`whitespace-nowrap text-xs ${s.onderdrukt ? 'text-amber-800' : 'text-gray-700'}`}>
                          {s.onderdrukt
                            ? rijLaag === 'intern'
                              ? `⏳ ${t('dataCockpit.reviews.status.verzamelend', { x: s.aantal, n })}`
                              : `⏳ ${t('dataCockpit.reviews.status.landelijkTeWeinig', { n, org: d.minimum_organisaties })}`
                            : `${LAAG_ICOON[rijLaag]} ${t(`dataCockpit.reviews.status.${rijLaag}`, { count: s.aantal })}`}
                        </p>
                      </td>
                      <td className="py-2.5">
                        {s.onderdrukt ? (
                          <span className="whitespace-nowrap text-xs text-gray-500">
                            <Lock className="mr-1 inline h-3 w-3" aria-hidden />
                            {t(`dataCockpit.reviews.vergrendeld.${rijLaag}`)}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setOpen({ rij: r, laag: rijLaag })}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-[#0B1F3A] hover:bg-blue-50"
                          >
                            <FileText className="h-3.5 w-3.5" aria-hidden />
                            {t(`dataCockpit.reviews.open.${rijLaag}`, { count: s.aantal })}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-xs text-gray-500">{t('dataCockpit.reviews.voetnoot', { n, org: d.minimum_organisaties })}</p>
      </Kaart>
      {open && <TekstenModal rij={open.rij} laag={open.laag} onSluit={sluit} />}
    </>
  );
};

/** Submap 5 — Pool-Inzet & Professional Reviews. */
export const DataPoolPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const [tab, setTab] = useState<'inzet' | 'reviews'>('inzet');
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <DataKop titel={t('dataCockpit.pool.titel')} ondertitel={t('dataCockpit.pool.ondertitel')} icoon={<Users className="h-6 w-6" />} />
      <DataFilterBalk />
      <div className="mb-4 flex gap-1 border-b border-gray-200" role="tablist">
        {(['inzet', 'reviews'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === k ? 'border-[#0B1F3A] text-[#0B1F3A]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          >
            {t(`dataCockpit.pool.tab.${k}`)}
          </button>
        ))}
      </div>
      {tab === 'inzet' ? <InzetTab /> : <ReviewsTab />}
    </div>
  );
};

export default DataPoolPage;
